import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, RefreshCw, ListTodo, PlayCircle, CheckCircle2, XCircle, Search, ChevronUp, ChevronDown, ChevronsUpDown, FolderKanban, FileText } from 'lucide-react';
import { t } from '@/i18n/ro';
import { useAuth } from '@/context/AuthContext';
import { useMetadata } from '@/context/MetadataContext';
import { getMyWorkPackages, getStatusIdFromLink } from '@/services/workpackages.service';
import { getAccessibleProjects } from '@/services/projects.service';
import {
  Button,
  Select,
  Card,
  LoadingState,
  EmptyState,
} from '@/components/common';
import { extractIdFromLink } from '@/services/api';
import { SearchableSelect } from '../common/SearchableSelect';
import type { WorkPackage, Project } from '@/types';

export type ActivityFilter = 'todo' | 'in-progress' | 'completed' | 'cancelled';

interface ActivitiesFilteredPageProps {
  filter: ActivityFilter;
}

const filterConfig: Record<ActivityFilter, {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  emptyMessage: string;
}> = {
  'todo': {
    title: 'Activități de făcut',
    description: 'Activitățile care așteaptă să fie începute',
    icon: <ListTodo className="w-6 h-6" />,
    color: 'bg-amber-500',
    emptyMessage: 'Nu aveți activități de făcut.',
  },
  'in-progress': {
    title: 'Activități în lucru',
    description: 'Activitățile la care lucrați în acest moment',
    icon: <PlayCircle className="w-6 h-6" />,
    color: 'bg-blue-500',
    emptyMessage: 'Nu aveți activități în lucru.',
  },
  'completed': {
    title: 'Activități finalizate',
    description: 'Activitățile pe care le-ați finalizat',
    icon: <CheckCircle2 className="w-6 h-6" />,
    color: 'bg-green-500',
    emptyMessage: 'Nu aveți activități finalizate.',
  },
  'cancelled': {
    title: 'Activități respinse',
    description: 'Activitățile respinse, anulate sau abandonate',
    icon: <XCircle className="w-6 h-6" />,
    color: 'bg-rose-500',
    emptyMessage: 'Nu aveți activități respinse.',
  },
};

type SortField = 'subject' | 'project' | 'priority' | 'dueDate' | 'filesCount';
type SortDirection = 'asc' | 'desc';

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
}

export function ActivitiesFilteredPage({ filter }: ActivitiesFilteredPageProps) {
  const { user } = useAuth();
  const { types, statusGroups, isLoading: metadataLoading, getStatusById, getPriorityById } = useMetadata();
  const navigate = useNavigate();

  const [workPackages, setWorkPackages] = useState<WorkPackage[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtre
  const [selectedProject, setSelectedProject] = useState<number | ''>('');
  const [selectedType, setSelectedType] = useState<number | ''>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Sort state
  const [sortField, setSortField] = useState<SortField>('dueDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const config = filterConfig[filter];

  // Încărcare date
  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [wpData, projectsData] = await Promise.all([
        getMyWorkPackages({
          projectId: selectedProject || undefined,
          typeId: selectedType || undefined,
        }),
        getAccessibleProjects(),
      ]);

      setWorkPackages(wpData);
      setProjects(projectsData);
    } catch (err) {
      console.error('Failed to load activities data:', err);
      setError(t('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedProject, selectedType]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedProject, selectedType]);

  // Filtrare work packages după status group și search
  const filteredWorkPackages = useMemo(() => {
    let statusIds: Set<number>;

    switch (filter) {
      case 'todo':
        statusIds = new Set(statusGroups.todo.map((s) => s.id));
        break;
      case 'in-progress':
        statusIds = new Set(statusGroups.inProgress.map((s) => s.id));
        break;
      case 'completed':
        statusIds = new Set(statusGroups.completed.map((s) => s.id));
        break;
      case 'cancelled':
        statusIds = new Set(statusGroups.cancelled.map((s) => s.id));
        break;
    }

    let result = workPackages.filter((wp) => {
      const statusId = getStatusIdFromLink(wp);
      return statusId && statusIds.has(statusId);
    });

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(wp =>
        wp.subject.toLowerCase().includes(query) ||
        wp._links.project?.title?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [workPackages, statusGroups, filter, searchQuery]);

  // Sort work packages
  const sortedWorkPackages = useMemo(() => {
    const sorted = [...filteredWorkPackages];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'subject':
          comparison = a.subject.localeCompare(b.subject, 'ro');
          break;
        case 'project':
          const projectA = a._links.project?.title || '';
          const projectB = b._links.project?.title || '';
          comparison = projectA.localeCompare(projectB, 'ro');
          break;
        case 'priority':
          const priorityIdA = extractIdFromLink(a._links.priority);
          const priorityIdB = extractIdFromLink(b._links.priority);
          const priorityA = priorityIdA ? getPriorityById(priorityIdA)?.position || 0 : 0;
          const priorityB = priorityIdB ? getPriorityById(priorityIdB)?.position || 0 : 0;
          comparison = priorityA - priorityB;
          break;
        case 'dueDate':
          const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
          const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
          comparison = dateA - dateB;
          break;
        case 'filesCount':
          comparison = (a.attachmentCount || 0) - (b.attachmentCount || 0);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredWorkPackages, sortField, sortDirection, getStatusById, getPriorityById]);

  // Pagination
  const totalPages = Math.ceil(sortedWorkPackages.length / pageSize);
  const paginatedWorkPackages = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedWorkPackages.slice(start, start + pageSize);
  }, [sortedWorkPackages, currentPage, pageSize]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortDirection === 'asc'
      ? <ChevronUp className="w-4 h-4 text-primary-600" />
      : <ChevronDown className="w-4 h-4 text-primary-600" />;
  };

  // Opțiuni pentru filtre
  const projectOptions = [
    { value: '', label: t('dashboard.filters.allProjects') },
    ...projects.map((p) => ({ value: p.id, label: p.name })),
  ];

  const typeOptions = [
    { value: '', label: t('dashboard.filters.allTypes') },
    ...types.map((t) => ({ value: t.id, label: t.name })),
  ];

  const handleWorkPackageClick = (wp: WorkPackage) => {
    navigate(`/work-packages/${wp.id}`);
  };

  const handleProjectClick = (e: React.MouseEvent, projectId: number) => {
    e.stopPropagation();
    navigate(`/projects/${projectId}`);
  };

  if (isLoading || metadataLoading) {
    return <LoadingState message={t('common.loading')} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-lg ${config.color} text-white`}>
            {config.icon}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {config.title}
            </h1>
            <p className="text-gray-600 mt-1">
              {config.description}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full font-medium">
            {filteredWorkPackages.length} {filteredWorkPackages.length === 1 ? 'activitate' : 'activități'}
          </span>
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={loadData}
          >
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {/* Table Card */}
      <Card className="overflow-hidden">
        {/* Search, Filters and page size */}
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Caută activități..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <Select
                options={projectOptions}
                value={selectedProject}
                onChange={(e) =>
                  setSelectedProject(e.target.value ? Number(e.target.value) : '')
                }
                className="min-w-[180px]"
              />
              <Select
                options={typeOptions}
                value={selectedType}
                onChange={(e) =>
                  setSelectedType(e.target.value ? Number(e.target.value) : '')
                }
                className="min-w-[150px]"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Afișează</span>
            <SearchableSelect
              clearable={false}
              options={[
                { value: 10, label: '10' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
              ]}
              value={pageSize}
              onChange={(v) => {
                setPageSize(Number(v));
                setCurrentPage(1);
              }}
              className="w-24"
            />
            <span className="text-sm text-gray-500">înregistrări</span>
          </div>
        </div>

        {/* Table */}
        {sortedWorkPackages.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon="calendar"
              title={config.emptyMessage}
              description="Încercați să modificați filtrele sau reveniți mai târziu."
            />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('subject')}
                    >
                      <div className="flex items-center gap-2">
                        Subiect
                        <SortIcon field="subject" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('project')}
                    >
                      <div className="flex items-center gap-2">
                        Proiect
                        <SortIcon field="project" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('priority')}
                    >
                      <div className="flex items-center gap-2">
                        Prioritate
                        <SortIcon field="priority" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('dueDate')}
                    >
                      <div className="flex items-center gap-2">
                        Termen
                        <SortIcon field="dueDate" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('filesCount')}
                    >
                      <div className="flex items-center justify-center gap-2">
                        Fișiere
                        <SortIcon field="filesCount" />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedWorkPackages.map((wp) => {
                    const priorityId = extractIdFromLink(wp._links.priority);
                    const projectId = extractIdFromLink(wp._links.project);
                    const statusId = extractIdFromLink(wp._links.status);

                    const priority = priorityId ? getPriorityById(priorityId) : null;
                    const status = statusId ? getStatusById(statusId) : null;

                    const isOverdue = wp.dueDate && new Date(wp.dueDate) < new Date() && !status?.isClosed;

                    return (
                      <tr
                        key={wp.id}
                        onClick={() => handleWorkPackageClick(wp)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900 hover:text-primary-600">
                            {wp.subject}
                          </div>
                          {wp._links.assignee?.title && (
                            <div className="text-xs text-gray-500 mt-1">
                              Responsabil: {wp._links.assignee.title}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {projectId ? (
                            <button
                              onClick={(e) => handleProjectClick(e, projectId)}
                              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 hover:underline"
                            >
                              <FolderKanban className="w-4 h-4" />
                              {wp._links.project?.title || '-'}
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {priority ? (
                            <span
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: priority.color ? `${priority.color}20` : '#FEF3C7',
                                color: priority.color || '#92400E',
                              }}
                            >
                              {priority.name}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                            {formatDate(wp.dueDate)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <FileText className="w-4 h-4 text-orange-500" />
                            <span className="text-sm font-medium text-gray-700">
                              {wp.attachmentCount || 0}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-500">
                Afișare {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, sortedWorkPackages.length)} din {sortedWorkPackages.length} activități
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Prima
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <span className="px-3 py-1.5 text-sm bg-primary-50 text-primary-700 border border-primary-200 rounded-lg font-medium">
                  {currentPage} / {totalPages || 1}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Următor
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ultima
                </button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
